import React from 'react';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../ui/dialog';
import { Plus, Upload, Link as LinkIcon, X } from 'lucide-react';

export const ArtistFormDialog = ({
  isOpen, onOpenChange, editingArtist,
  formData, setFormData, imagePreview, uploading,
  customType, setCustomType, showCustomInput, setShowCustomInput,
  handleChange, handleFileUpload, handleSubmit
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700">
          <Plus className="w-4 h-4 mr-2" /> Add Artist
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-black border-green-500/30 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">{editingArtist ? 'Edit Artist' : 'Add New Artist'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Artist Name</label>
            <Input name="name" value={formData.name} onChange={handleChange} required className="bg-black/50 border-green-500/30" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Bio</label>
            <Textarea name="bio" value={formData.bio} onChange={handleChange} required rows={4} className="bg-black/50 border-green-500/30 resize-none" />
          </div>
          
          {/* Type selector */}
          <div>
            <label className="block text-sm font-medium mb-2">Artist Type(s)</label>
            <div className="bg-black/50 border border-green-500/30 rounded-md p-4 space-y-3">
              {formData.type.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {formData.type.map((type) => (
                    <Badge key={type} variant="secondary" className="bg-green-500/20 text-green-300 border-green-500/50 pr-1">
                      {type}
                      <button type="button" onClick={() => setFormData({...formData, type: formData.type.filter(t => t !== type)})}
                        className="ml-2 hover:bg-green-500/30 rounded-full p-0.5"><X size={14} /></button>
                    </Badge>
                  ))}
                </div>
              )}
              <div className="space-y-2">
                {['DJ', 'Producer'].map((typeOption) => (
                  <label key={typeOption} className="flex items-center gap-3 cursor-pointer hover:bg-green-500/10 p-2 rounded transition-colors">
                    <input type="checkbox" checked={formData.type.includes(typeOption)}
                      onChange={(e) => {
                        if (e.target.checked) setFormData({...formData, type: [...formData.type, typeOption]});
                        else setFormData({...formData, type: formData.type.filter(t => t !== typeOption)});
                      }} className="w-4 h-4 accent-green-500" />
                    <span className="text-white">{typeOption}</span>
                  </label>
                ))}
                <label className="flex items-center gap-3 cursor-pointer hover:bg-green-500/10 p-2 rounded transition-colors">
                  <input type="checkbox" checked={showCustomInput} onChange={(e) => setShowCustomInput(e.target.checked)} className="w-4 h-4 accent-green-500" />
                  <span className="text-white">Custom Type</span>
                </label>
              </div>
              {showCustomInput && (
                <Input value={customType} onChange={(e) => setCustomType(e.target.value)}
                  placeholder="Enter custom types (comma-separated)" className="mt-2 bg-black/30 border-green-500/30" />
              )}
            </div>
          </div>

          {/* Image */}
          <div>
            <label className="block text-sm font-medium mb-2">Image</label>
            <Tabs defaultValue="url" className="w-full">
              <TabsList className="grid w-full grid-cols-2 bg-black/50">
                <TabsTrigger value="url"><LinkIcon className="w-4 h-4 mr-2" /> URL</TabsTrigger>
                <TabsTrigger value="upload"><Upload className="w-4 h-4 mr-2" /> Upload</TabsTrigger>
              </TabsList>
              <TabsContent value="url" className="space-y-2">
                <Input name="image" value={formData.image} onChange={handleChange} placeholder="https://example.com/image.jpg" className="bg-black/50 border-green-500/30" />
              </TabsContent>
              <TabsContent value="upload" className="space-y-2">
                <Input type="file" accept="image/*" onChange={handleFileUpload} disabled={uploading} className="bg-black/50 border-green-500/30" />
                {uploading && <p className="text-sm text-orange-400">Uploading...</p>}
              </TabsContent>
            </Tabs>
            {imagePreview && <div className="mt-2"><img src={imagePreview} alt="Preview" className="w-full h-64 object-cover rounded border border-green-500/30" /></div>}
          </div>

          {/* Links */}
          <div>
            <label className="block text-sm font-medium mb-2">SoundCloud URL</label>
            <Input name="soundcloudUrl" value={formData.soundcloudUrl} onChange={handleChange} className="bg-black/50 border-green-500/30" placeholder="https://soundcloud.com/artist" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">External Link (Website/Bio)</label>
            <Input name="externalLink" value={formData.externalLink} onChange={handleChange} className="bg-black/50 border-green-500/30" placeholder="https://artist-website.com" />
          </div>
          
          <div className="border-t border-green-500/20 pt-4">
            <h3 className="text-lg font-semibold mb-4 text-green-400">Additional Social Media (Optional)</h3>
            <div className="grid grid-cols-2 gap-4">
              {[
                { name: 'instagram', label: 'Instagram', ph: 'https://instagram.com/artist' },
                { name: 'facebook', label: 'Facebook', ph: 'https://facebook.com/artist' },
                { name: 'spotify', label: 'Spotify', ph: 'https://open.spotify.com/artist/...' },
                { name: 'youtube', label: 'YouTube', ph: 'https://youtube.com/@artist' },
              ].map(({ name, label, ph }) => (
                <div key={name}>
                  <label className="block text-sm font-medium mb-2">{label}</label>
                  <Input name={name} value={formData[name]} onChange={handleChange} className="bg-black/50 border-green-500/30" placeholder={ph} />
                </div>
              ))}
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-2">Bandcamp</label>
                <Input name="bandcamp" value={formData.bandcamp} onChange={handleChange} className="bg-black/50 border-green-500/30" placeholder="https://artist.bandcamp.com" />
              </div>
            </div>
          </div>
          
          <Button type="submit" disabled={uploading} className="w-full bg-gradient-to-r from-orange-500 to-green-500 hover:from-orange-600 hover:to-green-600">
            {editingArtist ? 'Update Artist' : 'Create Artist'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
